import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Search,
  Users,
  Building2,
  Phone,
  Mail,
  MapPin,
  Eye,
  Edit,
  UserPlus,
  Filter,
  Download,
  TrendingUp,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Heart,
  Sparkles,
  Trash2,
  AlertTriangle,
  RefreshCw,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { UserMenu } from '../components/UserMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_ALIADO = {
  referidor: { label: 'Aliado Referidor', icon: Heart },
  wedding_planner: { label: 'Wedding Planner', icon: Calendar },
  hotel: { label: 'Hotel', icon: Building2 },
  hacienda: { label: 'Hacienda', icon: Building2 },
  fotografo: { label: 'Fotógrafo', icon: Users },
  organizador_eventos: { label: 'Organizador de Eventos', icon: Calendar },
  productora: { label: 'Productora', icon: Building2 },
  proveedor: { label: 'Proveedor', icon: Building2 },
  aliado_bodas: { label: 'Aliado Bodas', icon: Heart },
  otro: { label: 'Otro', icon: Users },
  pendiente_perfil: { label: 'Pendiente de Perfil', icon: AlertTriangle }
};

const ESTADOS_ALIADO = {
  activo: { label: 'Activo', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  inactivo: { label: 'Inactivo', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
  suspendido: { label: 'Suspendido', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  sin_perfil: { label: 'Sin Perfil', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
};

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

const AliadosList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [aliados, setAliados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  // Estado para sincronización
  const [usuariosSinPerfil, setUsuariosSinPerfil] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  
  // Estado para eliminar aliado
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [aliadoAEliminar, setAliadoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const fetchAliados = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterTipo) params.append('tipo', filterTipo);
      if (filterEstado) params.append('estado', filterEstado);
      params.append('incluir_sin_perfil', 'true');
      
      const response = await axios.get(`${API_URL}/api/aliados?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliados(response.data.aliados || []);
      setUsuariosSinPerfil(response.data.usuarios_sin_perfil || 0);
    } catch (error) {
      toast.error('Error al cargar los aliados');
    } finally {
      setLoading(false);
    }
  }, [search, filterTipo, filterEstado]);

  // Función para sincronizar aliados
  const sincronizarAliados = async () => {
    setSincronizando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/aliados/sincronizar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.total_creados > 0) {
        toast.success(`${response.data.total_creados} perfiles de aliado creados correctamente`);
      } else {
        toast.info('Todos los usuarios aliados ya tienen su perfil');
      }
      
      // Recargar datos
      fetchAliados();
      if (user?.role === 'gerencia' || user?.role === 'admin') {
        fetchDashboard();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al sincronizar aliados';
      toast.error(errorMsg);
    } finally {
      setSincronizando(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.debug('Dashboard not available for this user');
    }
  }, []);

  // Función para eliminar aliado
  const eliminarAliado = async () => {
    if (!aliadoAEliminar) return;
    
    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/aliados/${aliadoAEliminar.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Aliado "${aliadoAEliminar.nombre}" eliminado correctamente`);
      setShowDeleteModal(false);
      setAliadoAEliminar(null);
      fetchAliados();
      if (user?.role === 'gerencia' || user?.role === 'admin') {
        fetchDashboard();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al eliminar el aliado';
      toast.error(errorMsg);
    } finally {
      setEliminando(false);
    }
  };

  // Abrir modal de confirmación
  const confirmarEliminacion = (e, aliado) => {
    e.stopPropagation();
    setAliadoAEliminar(aliado);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchAliados();
    if (user?.role === 'gerencia' || user?.role === 'admin') {
      fetchDashboard();
    }
  }, [fetchAliados, fetchDashboard, user?.role]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAliados();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchAliados]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(/images/novio-mascota.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/30 via-zinc-950/80 to-zinc-950" />
        
        <header className="relative bg-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">Dashboard</span>
                </button>
                <div className="h-6 w-px bg-zinc-700" />
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <h1 className="font-display text-xl">Aliados y Proveedores</h1>
                </div>
              </div>
              
              <div className="flex gap-2">
                {/* Botón de sincronización - Solo si hay usuarios sin perfil */}
                {usuariosSinPerfil > 0 && (user?.role === 'gerencia' || user?.role === 'admin') && (
                  <button
                    onClick={sincronizarAliados}
                    disabled={sincronizando}
                    className="flex items-center gap-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 px-4 py-2.5 rounded-lg hover:bg-amber-500/30 transition-colors"
                    title={`${usuariosSinPerfil} usuario(s) aliado(s) sin perfil`}
                    data-testid="sincronizar-aliados-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">
                      {sincronizando ? 'Sincronizando...' : `Sincronizar (${usuariosSinPerfil})`}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => navigate('/descargas')}
                  className="flex items-center gap-2 border border-zinc-700 text-zinc-300 px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Descargas</span>
                </button>
                <button
                  onClick={() => navigate('/referidos')}
                  className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-2.5 rounded-lg hover:bg-[#D4AF37]/10 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Ver Referidos</span>
                </button>
                <button
                  onClick={() => navigate('/aliados/nuevo')}
                  className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 rounded-lg hover:bg-[#C49F27] transition-all hover:scale-105 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nuevo Aliado</span>
                </button>
                
                {/* User Menu */}
                <UserMenu variant="dark" />
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-500/20 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Aliados Activos</p>
                    <p className="text-2xl font-display">{dashboard.aliados.activos}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-green-500/20 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Referidos</p>
                    <p className="text-2xl font-display">{dashboard.referidos.total}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-[#D4AF37]/20 rounded-lg">
                    <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Comisiones Pendientes</p>
                    <p className="text-xl font-display text-[#D4AF37]">{formatPrice(dashboard.comisiones.pendientes)}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-pink-500/20 rounded-lg">
                    <Heart className="w-5 h-5 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Weddings</p>
                    <p className="text-2xl font-display">{dashboard.weddings.total}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filtros y búsqueda */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Buscar por nombre, correo o documento..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white placeholder-zinc-500"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
                showFilters || filterTipo || filterEstado
                  ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                  : 'border-zinc-700 hover:bg-zinc-800 hover:border-zinc-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {(filterTipo || filterEstado) && (
                <span className="bg-black/20 px-1.5 py-0.5 rounded text-xs">
                  {[filterTipo, filterEstado].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
          
          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800">
              <select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
              >
                <option value="">Todos los tipos</option>
                {Object.entries(TIPOS_ALIADO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
              
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
              >
                <option value="">Todos los estados</option>
                {Object.entries(ESTADOS_ALIADO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
              
              {(filterTipo || filterEstado) && (
                <button
                  onClick={() => { setFilterTipo(''); setFilterEstado(''); }}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Alerta de sincronización */}
        {usuariosSinPerfil > 0 && (user?.role === 'gerencia' || user?.role === 'admin') && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-200 font-medium">
                {usuariosSinPerfil} usuario(s) aliado(s) sin perfil
              </p>
              <p className="text-amber-200/70 text-sm">
                Hay usuarios aprobados con rol "Aliado" que no tienen perfil creado en el sistema. 
                Haz clic en "Sincronizar" para crear automáticamente sus perfiles.
              </p>
            </div>
            <button
              onClick={sincronizarAliados}
              disabled={sincronizando}
              className="flex items-center gap-2 bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors font-medium text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
              {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
        )}

        {/* Lista de aliados */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
              <p className="text-zinc-400">Cargando aliados...</p>
            </div>
          </div>
        ) : aliados.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-16 text-center">
            <Users className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <h3 className="font-display text-xl mb-2">No hay aliados registrados</h3>
            <p className="text-zinc-400 mb-6">Comienza agregando tu primer aliado o proveedor</p>
            <button
              onClick={() => navigate('/aliados/nuevo')}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 rounded-lg hover:bg-[#C49F27] transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Nuevo Aliado
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {aliados.map((aliado) => {
              const tipoInfo = TIPOS_ALIADO[aliado.tipo_aliado] || TIPOS_ALIADO.otro;
              const estadoInfo = ESTADOS_ALIADO[aliado.estado] || ESTADOS_ALIADO.activo;
              const TipoIcon = tipoInfo.icon;
              const requiereCrearPerfil = aliado.requiere_crear_perfil === true;
              
              return (
                <div
                  key={aliado.id}
                  className={`bg-zinc-900/50 border rounded-xl p-6 hover:border-zinc-700 transition-all group cursor-pointer ${
                    requiereCrearPerfil ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-800'
                  }`}
                  onClick={() => !requiereCrearPerfil && navigate(`/aliados/${aliado.id}`)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Info principal */}
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl group-hover:bg-[#D4AF37]/20 transition-colors ${
                        requiereCrearPerfil ? 'bg-amber-500/20' : 'bg-zinc-800'
                      }`}>
                        <TipoIcon className={`w-6 h-6 group-hover:text-[#D4AF37] transition-colors ${
                          requiereCrearPerfil ? 'text-amber-400' : 'text-zinc-400'
                        }`} />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="font-display text-lg group-hover:text-[#D4AF37] transition-colors">{aliado.nombre}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${estadoInfo.color}`}>
                            {estadoInfo.label}
                          </span>
                          {aliado.user_id && !requiereCrearPerfil && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                              Con acceso
                            </span>
                          )}
                          {requiereCrearPerfil && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
                              Requiere sincronización
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-[#D4AF37] mb-2">{tipoInfo.label}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                          {aliado.telefono && (
                            <span className="flex items-center gap-1.5">
                              <Phone className="w-4 h-4" />
                              {aliado.telefono}
                            </span>
                          )}
                          <span className="flex items-center gap-1.5">
                            <Mail className="w-4 h-4" />
                            {aliado.correo}
                          </span>
                          {aliado.ciudad && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="w-4 h-4" />
                              {aliado.ciudad}
                            </span>
                          )}
                        </div>
                        
                        {requiereCrearPerfil && (
                          <p className="text-xs text-amber-400/80 mt-2">
                            {aliado.mensaje}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Estadísticas - Solo si tiene perfil */}
                    {!requiereCrearPerfil && (
                      <div className="flex flex-wrap gap-6 lg:gap-8">
                        <div className="text-center">
                          <p className="text-2xl font-display">{aliado.total_referidos || 0}</p>
                          <p className="text-xs text-zinc-500">Referidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-display">{aliado.total_ventas || 0}</p>
                          <p className="text-xs text-zinc-500">Ventas</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-display text-[#D4AF37]">{formatPrice(aliado.total_comisiones_generadas || 0)}</p>
                          <p className="text-xs text-zinc-500">Comisiones</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Acciones */}
                    <div className="flex gap-2">
                      {requiereCrearPerfil ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); sincronizarAliados(); }}
                          disabled={sincronizando}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black hover:bg-amber-400 rounded-lg transition-colors text-sm font-medium"
                        >
                          <UserCheck className="w-4 h-4" />
                          Crear Perfil
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/aliados/${aliado.id}`); }}
                            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Ver
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/aliados/${aliado.id}/referidos`); }}
                            className="flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:bg-zinc-800 rounded-lg transition-colors text-sm"
                          >
                            <Users className="w-4 h-4" />
                            Referidos
                          </button>
                          {!aliado.user_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/aliados/${aliado.id}`); }}
                              className="flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors text-sm"
                              title="Crear usuario de acceso"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          )}
                          {/* Botón eliminar - Solo Admin/Gerencia */}
                          {(user?.role === 'admin' || user?.role === 'gerencia') && (
                            <button
                              onClick={(e) => confirmarEliminacion(e, aliado)}
                              className="flex items-center gap-2 px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
                              title="Eliminar aliado"
                              data-testid={`eliminar-aliado-${aliado.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && aliadoAEliminar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Eliminar Aliado</h3>
                  <p className="text-sm text-zinc-400">Esta acción no se puede deshacer</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                <p className="text-white font-medium">{aliadoAEliminar.nombre}</p>
                <p className="text-sm text-zinc-400">{aliadoAEliminar.correo}</p>
                {aliadoAEliminar.id_usuario && (
                  <p className="text-xs text-amber-400 mt-2">
                    ⚠️ Este aliado tiene usuario de acceso asociado que también será eliminado
                  </p>
                )}
              </div>
              
              <p className="text-sm text-zinc-400 mb-6">
                ¿Estás seguro de que deseas eliminar este aliado? Se mantendrá un registro histórico de los referidos asociados.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setAliadoAEliminar(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                  disabled={eliminando}
                >
                  Cancelar
                </button>
                <button
                  onClick={eliminarAliado}
                  disabled={eliminando}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 rounded-lg transition-colors"
                  data-testid="confirmar-eliminar-aliado"
                >
                  {eliminando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AliadosList;
